import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { triggerHaptic } from '../../../utils/telegram';
import { shareFeedItem } from '../../../store/slices/feed/asyncThunks';
import { SHARE_PLATFORM_MAP } from '../constants';
import { buildReelShareUrl, shareToPlatform } from '../reelsPage.helpers';

const useReelShare = reels => {
  const dispatch = useDispatch();
  const [activeShareReelId, setActiveShareReelId] = useState(null);

  const activeShareReel = useMemo(
    () => reels.find(reel => reel.id === activeShareReelId) || null,
    [activeShareReelId, reels],
  );

  const openShare = useCallback(reelId => {
    triggerHaptic();
    setActiveShareReelId(reelId);
  }, []);

  const closeShare = useCallback(() => {
    setActiveShareReelId(null);
  }, []);

  const onShareOptionClick = useCallback(
    async platform => {
      if (!activeShareReel) {
        return;
      }

      triggerHaptic();
      const shareUrl = buildReelShareUrl(activeShareReel.id);
      const shareText = activeShareReel.caption || activeShareReel.name;
      const storyMediaUrl =
        activeShareReel.primaryMedia?.type === 'video'
          ? activeShareReel.primaryMedia?.previewUrl || activeShareReel.primaryMedia?.url || ''
          : activeShareReel.primaryMedia?.url || activeShareReel.primaryMedia?.previewUrl || '';

      try {
        const copiedLink = await shareToPlatform(platform, shareUrl, shareText, activeShareReel.name, storyMediaUrl);
        await dispatch(
          shareFeedItem({
            feedItemId: activeShareReel.id,
            platform: SHARE_PLATFORM_MAP[platform] || SHARE_PLATFORM_MAP.other,
          }),
        ).unwrap();

        if (copiedLink) {
          notifyInfo('Reel link copied');
        }

        closeShare();
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        notifyError(error.message || 'Unable to share this reel right now.');
      }
    },
    [activeShareReel, closeShare, dispatch],
  );

  return {
    isShareOpen: Boolean(activeShareReel),
    openShare,
    closeShare,
    onShareOptionClick,
  };
};

export default useReelShare;
